package com.klantenstop.web.pages.admin.categories.manage

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.klantenstop.web.components.ImageChooser
import com.klantenstop.web.components.layout.AdminPageLayout
import com.materialkobweb.components.toast.ToastManager
import com.klantenstop.web.navigation.Navigation
import com.materialkobweb.styles.MaterialColorVars
import com.materialkobweb.utils.rememberViewModel
import com.klantenstop.web.utils.withAlpha
import com.materialkobweb.components.widgets.DsEditableField
import com.materialkobweb.components.widgets.DsCheckboxInput
import com.materialkobweb.components.widgets.DsSpinner
import com.materialkobweb.components.widgets.FilledButton
import com.materialkobweb.components.widgets.OutlinedButton
import com.materialkobweb.components.widgets.SpinnerSize
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flex
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

@Page("/admin/categorieen/beheren")
@Composable
fun ManageCategoryPage() {
    val ctx = rememberPageContext()
    val categoryId = ctx.route.params["id"]
    val viewModel = rememberViewModel(cached = false) { ManageCategoryViewModel(categoryId) }
    val state by viewModel.state.collectAsState()
    val isEdit = categoryId != null

    LaunchedEffect(Unit) {
        viewModel.effect.collectLatest { effect ->
            when (effect) {
                is ManageCategoryContract.Effect.NavigateBack ->
                    ctx.router.navigateTo(Navigation.Screen.Admin.Categories.Index.route)

                is ManageCategoryContract.Effect.ShowError -> ToastManager.error(effect.message)
                is ManageCategoryContract.Effect.ShowSuccess -> ToastManager.success(effect.message)
            }
        }
    }

    AdminPageLayout(
        title = if (isEdit) "Categorie bewerken" else "Categorie aanmaken"
    ) {
        ManageCategoryContent(
            state = state,
            isEdit = isEdit,
            sendIntent = viewModel::sendIntent
        )
    }
}

@Composable
fun ManageCategoryContent(
    state: ManageCategoryContract.State,
    isEdit: Boolean,
    sendIntent: (ManageCategoryContract.Intent) -> Unit,
) {
    val breakpoint = rememberBreakpoint()

    if (state.isLoading) {
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            DsSpinner(size = SpinnerSize.Normal)
        }
        return
    }

    Column(
        modifier = Modifier
            .fillMaxWidth(if (breakpoint > Breakpoint.MD) 80.percent else 90.percent)
            .fillMaxHeight()
            .maxWidth(1000.px)
            .padding(leftRight = 24.px, topBottom = 58.px),
        verticalArrangement = Arrangement.spacedBy(24.px)
    ) {
        // Logo upload
        ImageChooser(
            modifier = Modifier.size(200.px),
            id = "category-logo",
            label = "Logo",
            imageUrl = state.logoUrl ?: state.category?.logo?.url,
            centerText = "Klik om een afbeelding te uploaden",
            isLoading = state.isImageLoading,
            onFileSelected = { file ->
                if (isEdit) {
                    sendIntent(ManageCategoryContract.Intent.UploadImage(file))
                } else {
                    sendIntent(ManageCategoryContract.Intent.UpdateLogoFile(file))
                }
            },
            onDeleteClicked = if (isEdit && state.category?.logo != null) {
                { sendIntent(ManageCategoryContract.Intent.RemoveImage) }
            } else null
        )

        // Name field
        DsEditableField(
            modifier = Modifier.fillMaxWidth(),
            id = "category-name",
            label = "Naam *",
            placeholder = "Naam van de categorie",
            value = state.name,
            valid = true,
            onValueChange = { sendIntent(ManageCategoryContract.Intent.UpdateName(it)) },
            required = true,
            backgroundColor = MaterialColorVars.SurfaceContainer.value(),
            focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
        )

        // Alt text field
        DsEditableField(
            modifier = Modifier.fillMaxWidth(),
            id = "category-alt-text",
            label = "Alt tekst afbeelding",
            placeholder = "Beschrijving van de afbeelding (voor toegankelijkheid)",
            value = state.altText,
            valid = true,
            onValueChange = { sendIntent(ManageCategoryContract.Intent.UpdateAltText(it)) },
            backgroundColor = MaterialColorVars.SurfaceContainer.value(),
            focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
        )

        // Highlighted flag
        DsCheckboxInput(
            id = "category-highlighted",
            label = "Uitgelicht op homepage",
            checked = state.highlighted,
            onCheckedChange = { sendIntent(ManageCategoryContract.Intent.UpdateHighlighted(it)) }
        )

        // Description field
        DsEditableField(
            modifier = Modifier.fillMaxWidth(),
            id = "category-description",
            label = "Korte beschrijving",
            placeholder = "Korte beschrijving",
            value = state.description,
            valid = true,
            onValueChange = { sendIntent(ManageCategoryContract.Intent.UpdateDescription(it)) },
            backgroundColor = MaterialColorVars.SurfaceContainer.value(),
            focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
        )

        // Action buttons
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.px)
        ) {
            OutlinedButton(
                modifier = Modifier.flex(1),
                enabled = !state.isSaving && !state.isImageLoading,
                onClick = { sendIntent(ManageCategoryContract.Intent.NavigateBack) }
            ) {
                Text("Annuleren")
            }
            FilledButton(
                modifier = Modifier.flex(1),
                enabled = state.hasChanged && !state.isSaving,
                onClick = { sendIntent(ManageCategoryContract.Intent.SaveCategory) }
            ) {
                if (state.isSaving) DsSpinner(size = SpinnerSize.Small) else Text("Opslaan")
            }
        }
    }
}
